import { Link, NavLink } from 'react-router-dom';
import {
  Activity,
  Layers,
  LayoutGrid,
  LineChart,
  PanelLeftClose,
  Search,
  Settings,
  Tags,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/contexts/AuthContext';
import { canManageWorkspace, userRoleLabel } from '@/lib/permissions';
import { BrandMark } from '@/components/primitives/BrandMark';
import { cn } from '@/lib/cn';
import { useCommandPalette } from './useCommandPalette';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobile?: boolean;
  onNavigate?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  adminOnly?: boolean;
  workspaceOnly?: boolean;
}

export function Sidebar({ collapsed, onToggle, mobile = false, onNavigate }: SidebarProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { open } = useCommandPalette();
  const groups: Array<{ label: string; items: NavItem[] }> = [
    {
      label: t('Tableau de bord', 'Dashboard'),
      items: [
        { to: '/', label: t('Aperçu', 'Overview'), icon: LayoutGrid, end: true },
        { to: '/analytics', label: 'Analytics', icon: LineChart },
      ],
    },
    {
      label: t('Bibliothèque', 'Library'),
      items: [
        { to: '/accounts', label: t('Comptes', 'Accounts'), icon: Users },
        { to: '/templates', label: 'Templates', icon: Layers },
      ],
    },
    {
      label: t('Administration', 'Administration'),
      items: [
        { to: '/identities', label: t('Identités', 'Identities'), icon: Tags, workspaceOnly: true },
        {
          to: '/settings',
          label: t('Paramètres', 'Settings'),
          icon: Settings,
          workspaceOnly: true,
        },
        { to: '/ops', label: t('Exploitation', 'Operations'), icon: Activity, adminOnly: true },
      ],
    },
  ];

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col bg-bg-1 transition-[width] duration-200',
        mobile
          ? 'min-h-full w-full pb-[env(safe-area-inset-bottom)]'
          : 'sticky top-0 h-dvh border-r border-line-1',
        !mobile && (collapsed ? 'w-16' : 'w-[240px]'),
      )}
    >
      <div
        className={cn(
          'flex min-h-[76px] items-center justify-between gap-2 px-5 py-4',
          collapsed && 'flex-col px-2',
        )}
      >
        <Link
          to="/"
          onClick={onNavigate}
          aria-label="IG Analytics"
          className="flex min-w-0 items-center gap-2.5"
        >
          <BrandMark className="size-8 rounded-[10px]" />
          {!collapsed && (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[14px] font-semibold tracking-tight text-text-1">
                IG Analytics
              </span>
              <span className="text-[10px] text-text-3">
                {t('Analyse Instagram', 'Instagram analytics')}
              </span>
            </span>
          )}
        </Link>
        {!mobile && (
          <button
            type="button"
            onClick={onToggle}
            className="flex size-7 shrink-0 items-center justify-center rounded-lg text-text-3 transition-colors hover:bg-bg-2 hover:text-text-1"
            aria-label={collapsed ? t('Développer', 'Expand') : t('Réduire', 'Collapse')}
          >
            <PanelLeftClose
              className={cn('size-3.5 transition-transform', collapsed && 'rotate-180')}
            />
          </button>
        )}
      </div>

      {!mobile && (
        <div className="px-3">
          <button
            type="button"
            onClick={open}
            aria-label={t('Rechercher', 'Search')}
            className={cn(
              'flex h-10 w-full items-center gap-2 rounded-[9px] border border-line-1 bg-bg-0 px-3 text-[12px] text-text-3 transition-colors hover:border-line-2 hover:text-text-1',
              collapsed && 'justify-center px-0',
            )}
          >
            <Search className="size-3.5 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('Rechercher…', 'Search…')}</span>
                <kbd className="rounded border border-line-1 bg-bg-1 px-1 py-0.5 font-mono text-[10px] text-text-4">
                  ⌘K
                </kbd>
              </>
            )}
          </button>
        </div>
      )}

      <nav
        aria-label={t('Navigation principale', 'Main navigation')}
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-3 py-6"
      >
        {groups.map((group) => {
          const items = group.items.filter(
            (item) =>
              (!item.adminOnly || user?.role === 'ADMIN') &&
              (!item.workspaceOnly || canManageWorkspace(user?.role)),
          );
          if (!items.length) return null;
          return (
            <div key={group.label} className="flex flex-col gap-1">
              {!collapsed && (
                <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.13em] text-text-4">
                  {group.label}
                </p>
              )}
              {items.map(({ icon: Icon, ...item }) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  aria-label={collapsed ? item.label : undefined}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group flex min-h-10 items-center gap-3 rounded-[9px] px-3 text-[13px] font-medium transition-colors',
                      isActive
                        ? 'bg-violet-soft text-violet-deep'
                        : 'text-text-2 hover:bg-bg-2 hover:text-text-1',
                      collapsed && 'justify-center px-0',
                      mobile && 'min-h-12 text-[14px]',
                    )
                  }
                >
                  <Icon className="size-4 shrink-0" strokeWidth={1.7} aria-hidden="true" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div
        className={cn('mx-3 mb-4 border-t border-line-1 pt-4', collapsed && 'flex justify-center')}
      >
        <div
          className={cn(
            'flex min-w-0 items-center gap-2.5 rounded-xl bg-bg-0 p-2.5',
            collapsed && 'bg-transparent p-0',
          )}
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-violet/15 bg-violet-soft text-[12px] font-semibold text-violet-deep">
            {user?.username.charAt(0).toUpperCase() ?? 'I'}
          </span>
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate text-[12px] font-medium text-text-1" title={user?.username}>
                {user?.username}
              </p>
              <p className="truncate text-[10px] text-text-3">
                {user ? userRoleLabel(user.role, t) : 'IG Analytics'}
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
